import Modal from '@/components/ui/Modal.tsx';
import Select from '@/components/ui/Select.tsx';
import { Language, MediaLink, Quality } from '@/types';
import { languageOptions, languages, qualityOptions, qualityTypes } from '@/constants';
import Input from '@/components/ui/Input.tsx';
import MultiSelect from '@/components/ui/MultiSelect.tsx';
import Button from '@/components/ui/Button.tsx';
import {useTranslation} from '@/hooks';
import { useForm } from 'react-hook-form';
import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Translation } from '@/locales';

type DisplayableLink = {
  id?: string;
  quality: Quality;
  url: string;
  audioLanguages?: string[];
  subtitleLanguages?: string[];
  active: boolean;
};

type ModalProps = {
  isModalOpen: boolean;
  handleModalClose: () => void;
  handleAddLink: (link: Omit<MediaLink, "id" | "createdDate" | "updatedDate">) => void;
  handleUpdateLink?: (linkId: string, link: Omit<MediaLink, "id" | "createdDate" | "updatedDate">) => void;
  editingLink?: DisplayableLink | null;
}

const addLinkSchema  = (t: Translation)  => z.object({
  quality: z.enum(qualityTypes, t.validation.required),
  url: z.url(t.validation.urlFormat),
  audioLanguages: z.array(z.enum(languages)).optional(),
  subtitleLanguages: z.array(z.enum(languages)).optional(),
  active: z.boolean()
});

type LinkFormData = z.infer<ReturnType<typeof addLinkSchema>>;

const LinkManagerModal = ({
  isModalOpen,
  handleModalClose,
  handleAddLink,
  handleUpdateLink,
  editingLink,
                          }: ModalProps) => {
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    trigger,
    formState: { errors }
  } = useForm<LinkFormData>({
    resolver: zodResolver(addLinkSchema(t)),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: editingLink ? {
      quality: editingLink.quality,
      url: editingLink.url,
      audioLanguages: editingLink.audioLanguages as Language[],
      subtitleLanguages: editingLink.subtitleLanguages as Language[],
      active: editingLink.active
    } : {
      quality: 'FHD',
      url: '',
      audioLanguages: [],
      subtitleLanguages: [],
      active: true
    }
  })

  const watchAudioLanguages = watch('audioLanguages');
  const watchSubtitleLanguages = watch('subtitleLanguages');

  useEffect(() => {
    if (editingLink) {
      reset({
        quality: editingLink.quality,
        url: editingLink.url,
        audioLanguages: editingLink.audioLanguages as Language[],
        subtitleLanguages: editingLink.subtitleLanguages as Language[] || [],
        active: editingLink.active
      });
    } else {
      reset({
        quality: 'FHD',
        url: '',
        audioLanguages: [],
        subtitleLanguages: [],
        active: true
      });
    }
  }, [editingLink, reset]);

  const onSubmit = (data: LinkFormData) => {
    if (editingLink && editingLink.id && handleUpdateLink) {
      handleUpdateLink(editingLink.id, data);
    } else {
      handleAddLink(data);
    }
    closeModal();
  }

  const closeModal = () => {
    handleModalClose();
    reset()
  }

  return (
    <Modal
      isOpen={isModalOpen}
      onClose={handleModalClose}
      title={editingLink ? t.links.editLink : t.links.addLink}
      size="xl"
    >
      <form
        id="link-form"
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-4">
        <div className="grid grid-cols-1 gap-4">
          <Select
            label={t.links.quality}
            {...register('quality')}
            error={errors.quality?.message}
            options={qualityOptions}
          />

          <Input
            label={t.links.url}
            {...register('url')}
            error={errors.url?.message}
            placeholder="https://example.com/stream"
          />

          <MultiSelect
            label={t.links.audioLanguage}
            options={languageOptions}
            error={errors.audioLanguages?.message}
            value={watchAudioLanguages?.map(l => l.toUpperCase()) || []}
            onChange={(audioLanguages) => {
              setValue('audioLanguages', audioLanguages as Language[])
              trigger('audioLanguages');
            }}
          />

          <MultiSelect
            label={t.links.subtitleLanguage}
            options={languageOptions}
            error={errors.subtitleLanguages?.message}
            value={watchSubtitleLanguages?.map(l => l.toUpperCase()) || []}
            onChange={(subtitleLanguages) => {
              setValue('subtitleLanguages', subtitleLanguages as Language[]);
              trigger('audioLanguages');
            }}
          />
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button
            type="button"
            variant="outline"
            onClick={closeModal}
          >
            {t.buttons.cancel}
          </Button>
          <Button
            form="link-form"
            type="button"
            variant="primary"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSubmit(onSubmit)(); // For react-hook-form
            }}
          >
            {editingLink ? t.links.editLink : t.links.addLink}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

export default LinkManagerModal;
